import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import type { CSSProperties, KeyboardEvent } from 'react'
import { Button, Modal, Spinner } from '@heroui/react'

import { DownloadIcon } from '@/components/ui/icons'
import { errorMessage } from '@/lib/api/api-error'

import { saveReportWorkbook } from '../api'
import type { ReportWorkbook } from '../api'

import {
  closeExcelPreview,
  currentExcelPreview,
  subscribeExcelPreview,
} from './excel-preview-store'
import { PREVIEW_ROW_LIMIT, readWorkbook } from './excel-sheet'
import type { GridCell, GridSheet } from './excel-sheet'

/*
 * The sheet itself is drawn as Excel draws it — white cells, grey gridlines and
 * headers, Excel's green for the selection — in both themes. It is a picture of
 * the file, and the file looks the same whatever theme the merchant browses in.
 * Only the chrome around it (dialog, buttons) follows the app theme.
 */
const EXCEL_GREEN = '#217346'
const GRIDLINE = '1px solid #e1e1e1'
const HEADER_BORDER = '1px solid #c8c8c8'
const FROZEN_EDGE = '1px solid #9b9b9b'

/** Row-number gutter and column-letter strip, as Excel sizes them. */
const GUTTER_WIDTH = 42
const HEADER_HEIGHT = 20

const ZOOM_STEPS = [0.5, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 2]

/** Mounted once in the shell; shows whichever workbook an export opened. */
export function ExcelPreviewHost() {
  const workbook = useSyncExternalStore(subscribeExcelPreview, currentExcelPreview)

  if (!workbook) return null

  return <ExcelPreviewDialog key={workbook.fileName} workbook={workbook} onClose={closeExcelPreview} />
}

/** The export's own failure, under the button row — never in place of the table. */
export function ExportFeedback({ exporter }: { exporter?: { error: unknown } }) {
  if (exporter?.error == null) return null

  return (
    <p role="alert" className="text-danger text-xs">
      {errorMessage(exporter.error, 'The export could not be prepared. Try again.')}
    </p>
  )
}

function ExcelPreviewDialog({ workbook, onClose }: { workbook: ReportWorkbook; onClose: () => void }) {
  const [sheets, setSheets] = useState<GridSheet[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [active, setActive] = useState(0)
  const [zoom, setZoom] = useState(1)

  useEffect(() => {
    let cancelled = false

    readWorkbook(workbook.blob)
      .then((read) => !cancelled && setSheets(read))
      .catch(() => !cancelled && setFailed(true))

    return () => {
      cancelled = true
    }
  }, [workbook])

  const sheet = sheets?.[active]

  function download() {
    saveReportWorkbook(workbook)
    onClose()
  }

  function stepZoom(direction: 1 | -1) {
    const index = ZOOM_STEPS.indexOf(zoom)
    setZoom(ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, index + direction))])
  }

  return (
    <Modal isOpen onOpenChange={(open) => !open && onClose()}>
      <Modal.Backdrop className="overlay-scope" variant="blur">
        <Modal.Container size="cover" placement="center">
          <Modal.Dialog className="flex h-[90vh] max-w-7xl flex-col overflow-hidden p-0">
            <Modal.CloseTrigger className="end-3 top-3 size-10" />

            <Modal.Header className="flex-row items-center gap-3 px-5 pt-4 pb-3 pe-14">
              <ExcelBadge />
              <div className="min-w-0">
                <Modal.Heading className="truncate text-base">{workbook.fileName}</Modal.Heading>
                <p className="text-muted text-xs">Read-only preview. This is the file you will download.</p>
              </div>
            </Modal.Header>

            <Modal.Body className="flex min-h-0 flex-1 flex-col p-0">
              {!sheets && !failed && (
                <div className="flex flex-1 items-center justify-center">
                  <Spinner />
                </div>
              )}

              {failed && (
                <div className="flex flex-1 items-center justify-center px-6">
                  <p role="alert" className="text-danger text-sm">
                    The preview could not be drawn. The file can still be downloaded.
                  </p>
                </div>
              )}

              {sheets && sheet && (
                <SheetView key={active} sheet={sheet} zoom={zoom} />
              )}

              {sheets && (
                <StatusBar
                  sheets={sheets}
                  active={active}
                  onSelect={setActive}
                  zoom={zoom}
                  onZoom={stepZoom}
                  onResetZoom={() => setZoom(1)}
                />
              )}
            </Modal.Body>

            <Modal.Footer className="px-5 py-3">
              <Button variant="ghost" onPress={onClose}>
                Cancel
              </Button>
              <Button variant="primary" onPress={download}>
                <DownloadIcon width={16} height={16} />
                Download Excel
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}

function ExcelBadge() {
  return (
    <span
      aria-hidden
      className="flex size-9 shrink-0 items-center justify-center rounded-md text-sm font-bold text-white"
      style={{ backgroundColor: EXCEL_GREEN }}
    >
      X
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* The sheet                                                                  */
/* -------------------------------------------------------------------------- */

interface Selection {
  row: number
  column: number
}

function SheetView({ sheet, zoom }: { sheet: GridSheet; zoom: number }) {
  const [selected, setSelected] = useState<Selection>({ row: 1, column: 1 })

  const rows = useMemo(() => sheet.rows.filter((row) => row.height > 0), [sheet])

  // Sticky offsets for the frozen panes: each frozen row sits below the ones above it.
  const frozenTop = useMemo(() => {
    const tops: number[] = []
    let top = HEADER_HEIGHT
    for (const row of sheet.rows.slice(0, sheet.frozenRows)) {
      tops.push(top)
      top += row.height
    }
    return tops
  }, [sheet])

  const frozenLeft = useMemo(() => {
    const lefts: number[] = []
    let left = GUTTER_WIDTH
    for (const column of sheet.columns.slice(0, sheet.frozenColumns)) {
      lefts.push(left)
      left += column.width
    }
    return lefts
  }, [sheet])

  const tableWidth = GUTTER_WIDTH + sheet.columns.reduce((sum, column) => sum + column.width, 0)
  const selectedCell = sheet.rows[selected.row - 1]?.cells[selected.column - 1]
  const address = `${sheet.columns[selected.column - 1]?.letter ?? 'A'}${selected.row}`

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const moves: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
      Enter: [1, 0],
      Tab: [0, event.shiftKey ? -1 : 1],
    }
    const move = moves[event.key]
    if (!move) return

    event.preventDefault()
    setSelected(({ row, column }) => ({
      row: Math.min(sheet.rows.length, Math.max(1, row + move[0])),
      column: Math.min(sheet.columns.length, Math.max(1, column + move[1])),
    }))
  }

  return (
    <>
      {/* Name box and formula bar */}
      <div className="flex shrink-0 items-stretch gap-2 border-y border-[#d6d6d6] bg-[#f3f3f3] px-2 py-1 text-[13px] text-[#222]">
        <div className="flex w-24 items-center border border-[#c8c8c8] bg-white px-2 font-mono">{address}</div>
        <div className="flex items-center px-1 font-serif text-[#777] italic">fx</div>
        <div className="flex min-w-0 flex-1 items-center truncate border border-[#c8c8c8] bg-white px-2">
          {selectedCell?.input ?? ''}
        </div>
      </div>

      {/* The grid */}
      <div
        tabIndex={0}
        role="grid"
        aria-label={sheet.name}
        onKeyDown={onKeyDown}
        className="min-h-0 flex-1 overflow-auto bg-white text-[#000] outline-none"
      >
        <table
          className="border-separate border-spacing-0"
          style={{ tableLayout: 'fixed', width: tableWidth, zoom }}
        >
          <colgroup>
            <col style={{ width: GUTTER_WIDTH }} />
            {sheet.columns.map((column) => (
              <col key={column.letter} style={{ width: column.width }} />
            ))}
          </colgroup>

          <thead>
            <tr style={{ height: HEADER_HEIGHT }}>
              <th
                className="sticky top-0 left-0 z-30 bg-[#f3f3f3]"
                style={{ borderRight: HEADER_BORDER, borderBottom: HEADER_BORDER }}
              />
              {sheet.columns.map((column, index) => {
                const isActive = index + 1 === selected.column
                return (
                  <th
                    key={column.letter}
                    scope="col"
                    className={`sticky top-0 z-20 overflow-hidden text-center text-[12px] font-normal ${
                      isActive ? 'bg-[#d3f0e0] text-[#0e5c2f]' : 'bg-[#f3f3f3] text-[#444]'
                    }`}
                    style={{
                      borderRight: HEADER_BORDER,
                      borderBottom: isActive ? `2px solid ${EXCEL_GREEN}` : HEADER_BORDER,
                      left: index < sheet.frozenColumns ? frozenLeft[index] : undefined,
                      zIndex: index < sheet.frozenColumns ? 25 : undefined,
                    }}
                  >
                    {column.width > 0 ? column.letter : ''}
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => {
              const rowFrozen = row.number <= sheet.frozenRows
              const lastFrozen = row.number === sheet.frozenRows
              const isActiveRow = row.number === selected.row

              return (
                <tr key={row.number} style={{ height: row.height }}>
                  <th
                    scope="row"
                    className={`sticky left-0 text-center align-middle text-[12px] font-normal ${
                      isActiveRow ? 'bg-[#d3f0e0] text-[#0e5c2f]' : 'bg-[#f3f3f3] text-[#444]'
                    }`}
                    style={{
                      top: rowFrozen ? frozenTop[row.number - 1] : undefined,
                      zIndex: rowFrozen ? 25 : 10,
                      borderRight: isActiveRow ? `2px solid ${EXCEL_GREEN}` : HEADER_BORDER,
                      borderBottom: lastFrozen ? FROZEN_EDGE : HEADER_BORDER,
                    }}
                  >
                    {row.number}
                  </th>

                  {row.cells.map((cell) =>
                    cell.covered ? null : (
                      <SheetCell
                        key={cell.column}
                        cell={cell}
                        height={row.height}
                        gridLines={sheet.gridLines}
                        isSelected={row.number === selected.row && cell.column === selected.column}
                        stickyTop={rowFrozen ? frozenTop[row.number - 1] : undefined}
                        stickyLeft={cell.column <= sheet.frozenColumns ? frozenLeft[cell.column - 1] : undefined}
                        frozenBottom={lastFrozen}
                        frozenRight={cell.column + cell.colSpan - 1 === sheet.frozenColumns}
                        onSelect={() => setSelected({ row: row.number, column: cell.column })}
                      />
                    ),
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>

        {sheet.totalRows > PREVIEW_ROW_LIMIT && (
          <p className="sticky left-0 px-3 py-2 text-xs text-[#555]">
            Showing the first {PREVIEW_ROW_LIMIT.toLocaleString()} of {sheet.totalRows.toLocaleString()} rows. The
            download holds all of them.
          </p>
        )}
      </div>
    </>
  )
}

interface SheetCellProps {
  cell: GridCell
  height: number
  gridLines: boolean
  isSelected: boolean
  stickyTop?: number
  stickyLeft?: number
  frozenBottom: boolean
  frozenRight: boolean
  onSelect: () => void
}

function SheetCell({
  cell,
  height,
  gridLines,
  isSelected,
  stickyTop,
  stickyLeft,
  frozenBottom,
  frozenRight,
  onSelect,
}: SheetCellProps) {
  const sticky = stickyTop !== undefined || stickyLeft !== undefined
  const grid = gridLines ? GRIDLINE : undefined

  const style: CSSProperties = {
    height,
    padding: '0 3px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
    fontFamily: 'Calibri, Carlito, Arial, sans-serif',
    fontSize: '15px',
    backgroundColor: sticky ? '#ffffff' : undefined,
    ...cell.style,
    borderRight: cell.style.borderRight ?? (frozenRight ? FROZEN_EDGE : grid),
    borderBottom: cell.style.borderBottom ?? (frozenBottom ? FROZEN_EDGE : grid),
    position: sticky ? 'sticky' : 'relative',
    top: stickyTop,
    left: stickyLeft,
    zIndex: sticky ? (stickyTop !== undefined && stickyLeft !== undefined ? 15 : 5) : undefined,
    outline: isSelected ? `2px solid ${EXCEL_GREEN}` : undefined,
    outlineOffset: -2,
    cursor: 'cell',
  }

  return (
    <td colSpan={cell.colSpan} rowSpan={cell.rowSpan} style={style} onMouseDown={onSelect}>
      {cell.filter ? (
        <div className="flex h-full items-end justify-between gap-1">
          <span className="min-w-0 overflow-hidden">{cell.text}</span>
          <FilterButton />
        </div>
      ) : (
        cell.text
      )}
    </td>
  )
}

function FilterButton() {
  return (
    <span
      aria-hidden
      className="mb-px flex size-[15px] shrink-0 items-center justify-center border border-[#adadad] bg-[#f6f6f6] text-[#444]"
    >
      <svg width="8" height="6" viewBox="0 0 8 6">
        <path d="M0 0h8L4 5z" fill="currentColor" />
      </svg>
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Sheet tabs and status bar                                                  */
/* -------------------------------------------------------------------------- */

interface StatusBarProps {
  sheets: GridSheet[]
  active: number
  onSelect: (index: number) => void
  zoom: number
  onZoom: (direction: 1 | -1) => void
  onResetZoom: () => void
}

function StatusBar({ sheets, active, onSelect, zoom, onZoom, onResetZoom }: StatusBarProps) {
  const sheet = sheets[active]

  return (
    <div className="flex shrink-0 items-stretch justify-between border-t border-[#d6d6d6] bg-[#f3f3f3] text-[12px] text-[#333]">
      <div role="tablist" className="flex min-w-0 overflow-x-auto">
        {sheets.map((s, index) => {
          const isActive = index === active
          return (
            <button
              key={s.name}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(index)}
              className={`border-r border-[#d6d6d6] px-4 py-1.5 whitespace-nowrap ${
                isActive ? 'bg-white font-semibold' : 'hover:bg-[#e6e6e6]'
              }`}
              style={isActive ? { color: EXCEL_GREEN, boxShadow: `inset 0 -2px 0 ${EXCEL_GREEN}` } : undefined}
            >
              {s.name}
            </button>
          )
        })}
      </div>

      <div className="flex items-center gap-3 px-3">
        <span className="text-[#555]">
          {sheet ? `${sheet.totalRows.toLocaleString()} rows` : ''}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Zoom out"
            onClick={() => onZoom(-1)}
            className="size-5 rounded hover:bg-[#e0e0e0]"
          >
            −
          </button>
          <button type="button" onClick={onResetZoom} className="w-11 text-center hover:underline">
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            aria-label="Zoom in"
            onClick={() => onZoom(1)}
            className="size-5 rounded hover:bg-[#e0e0e0]"
          >
            +
          </button>
        </div>
      </div>
    </div>
  )
}
